import type { FollowUpQuestion } from "@/types/chat-parts";
import type { WidgetOrigin } from "@/constants/widget-origin";
import { create } from "zustand";

export const CHAT_MODE = {
  INPUT: "input",
  ANSWERING: "answering",
} as const;

export type ChatMode = (typeof CHAT_MODE)[keyof typeof CHAT_MODE];

type ChatState = {
  suggestedQuestions: FollowUpQuestion[];
  userQuery: string;
  contextUrl?: string;
  widgetOrigin?: WidgetOrigin;
  mode: ChatMode;

  // Actions
  setUserQuery: (query: string) => void;
  setContextUrl: (url: string | undefined) => void;
  setWidgetOrigin: (origin: WidgetOrigin | undefined) => void;
  clearUserQuery: () => void;
  clearContextUrl: () => void;
  setMode: (mode: ChatMode) => void;
  resetState: () => void;
  submitQuery: (query: string, origin?: WidgetOrigin) => void;
};

// Initial state
const initialState = {
  suggestedQuestions: [],
  userQuery: "",
  contextUrl: undefined,
  widgetOrigin: undefined,
  mode: CHAT_MODE.INPUT,
};

export const useChatState = create<ChatState>(set => ({
  ...initialState,

  // Update user query
  setUserQuery: (query: string) =>
    set(() => ({ userQuery: query })),

  // Update context URL
  setContextUrl: (url: string | undefined) =>
    set(() => ({ contextUrl: url })),

  // Update widget origin
  setWidgetOrigin: (origin: WidgetOrigin | undefined) =>
    set(() => ({ widgetOrigin: origin })),

  // Clear user query
  clearUserQuery: () =>
    set(() => ({ userQuery: "" })),

  // Clear context URL
  clearContextUrl: () =>
    set(() => ({ contextUrl: undefined })),

  // Update chat mode
  setMode: (mode: ChatMode) =>
    set(() => ({ mode })),

  // Reset entire state
  resetState: () =>
    set(() => ({ ...initialState })),

  submitQuery: (query: string, origin?: WidgetOrigin) =>
    set(() => ({
      userQuery: query,
      widgetOrigin: origin,
      contextUrl: undefined,
      mode: CHAT_MODE.INPUT,
    })),
}));
